import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';

const LoginScreen: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('🔐 LoginScreen: Starting login for:', username);

    try {
      await login(username, password);
      console.log('✅ LoginScreen: Login successful!');
      // Don't redirect here - let App component handle it

    } catch (err: any) {
      console.error('❌ LoginScreen: Login failed:', err);
      setError(err.message || 'Login failed. Please check your credentials.');
      setLoading(false); // Only set loading to false on error
    }
  };

  return (
    <>
      <style>{`
        @keyframes swirl1 {
          0% {
            transform: translate(0%, 0%) rotate(0deg) scale(1);
          }
          25% {
            transform: translate(30%, -20%) rotate(90deg) scale(1.2);
          }
          50% {
            transform: translate(0%, -40%) rotate(180deg) scale(1);
          }
          75% {
            transform: translate(-30%, -20%) rotate(270deg) scale(1.1);
          }
          100% {
            transform: translate(0%, 0%) rotate(360deg) scale(1);
          }
        }

        @keyframes swirl2 {
          0% {
            transform: translate(0%, 0%) rotate(0deg) scale(1);
          }
          25% {
            transform: translate(-40%, 30%) rotate(-90deg) scale(1.3);
          }
          50% {
            transform: translate(-20%, 0%) rotate(-180deg) scale(1.1);
          }
          75% {
            transform: translate(40%, 20%) rotate(-270deg) scale(1.2);
          }
          100% {
            transform: translate(0%, 0%) rotate(-360deg) scale(1);
          }
        }

        @keyframes swirl3 {
          0% {
            transform: translate(0%, 0%) rotate(45deg) scale(1);
          }
          33% {
            transform: translate(25%, 35%) rotate(165deg) scale(1.4);
          }
          66% {
            transform: translate(-35%, -25%) rotate(285deg) scale(1.15);
          }
          100% {
            transform: translate(0%, 0%) rotate(405deg) scale(1);
          }
        }

        @keyframes swirl4 {
          0% {
            transform: translate(0%, 0%) rotate(-30deg) scale(1);
          }
          33% {
            transform: translate(-30%, -30%) rotate(-150deg) scale(1.25);
          }
          66% {
            transform: translate(30%, 30%) rotate(-270deg) scale(1.35);
          }
          100% {
            transform: translate(0%, 0%) rotate(-390deg) scale(1);
          }
        }

        .animated-gradient {
          background: radial-gradient(circle at 50% 50%, #1e3a8a 0%, #1e40af 100%);
          position: relative;
          overflow: hidden;
        }

        .animated-gradient::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle at 30% 40%, #3b82f6 0%, rgba(59, 130, 246, 0) 50%),
                      radial-gradient(circle at 70% 60%, #60a5fa 0%, rgba(96, 165, 250, 0) 50%);
          animation: swirl1 20s ease-in-out infinite;
          opacity: 0.8;
        }

        .animated-gradient::after {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle at 60% 30%, #2563eb 0%, rgba(37, 99, 235, 0) 45%),
                      radial-gradient(circle at 40% 70%, #1d4ed8 0%, rgba(29, 78, 216, 0) 45%);
          animation: swirl2 18s ease-in-out infinite;
          opacity: 0.7;
        }

        .swirl-layer-1 {
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle at 50% 50%, #3b82f6 0%, rgba(59, 130, 246, 0) 40%),
                      radial-gradient(circle at 80% 20%, #93c5fd 0%, rgba(147, 197, 253, 0) 40%);
          animation: swirl3 22s ease-in-out infinite;
          opacity: 0.6;
        }

        .swirl-layer-2 {
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle at 20% 80%, #2563eb 0%, rgba(37, 99, 235, 0) 35%),
                      radial-gradient(circle at 90% 70%, #3b82f6 0%, rgba(59, 130, 246, 0) 35%);
          animation: swirl4 16s ease-in-out infinite;
          opacity: 0.5;
        }

        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f9fafb;
          padding: 1rem;
        }

        .login-card {
          width: 100%;
          max-width: 80rem;
          background: white;
          border-radius: 1.5rem;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          display: flex;
        }

        .login-left {
          width: 50%;
          position: relative;
          z-index: 10;
        }

        .login-left-content {
          position: relative;
          z-index: 20;
          padding: 3rem;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          height: 100%;
          color: white;
        }

        .login-right {
          width: 50%;
          padding: 3rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .brand-icon {
          width: 3rem;
          height: 3rem;
        }

        .brand-icon path {
          stroke: white;
          stroke-width: 2.5;
          stroke-linecap: round;
        }

        .tagline {
          font-size: 1.125rem;
          margin-bottom: 1rem;
          opacity: 0.95;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
        }

        .main-heading {
          font-size: 2.25rem;
          font-weight: bold;
          line-height: 1.2;
          filter: drop-shadow(0 4px 6px rgba(0,0,0,0.1));
        }

        .form-title-icon {
          width: 2rem;
          height: 2rem;
          margin-bottom: 1.5rem;
          color: #3b82f6;
        }

        .form-title-icon path {
          stroke: currentColor;
          stroke-width: 2.5;
          stroke-linecap: round;
        }

        .form-title {
          font-size: 1.875rem;
          font-weight: bold;
          color: #111827;
          margin-bottom: 0.5rem;
        }

        .form-subtitle {
          color: #6b7280;
          font-size: 0.875rem;
          margin-bottom: 2rem;
        }

        .input-label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: #111827;
          margin-bottom: 0.5rem;
        }

        .input-wrapper {
          margin-bottom: 1.25rem;
        }

        .p-inputtext,
        .p-password input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 0.5rem;
          font-size: 0.9375rem;
        }

        .p-inputtext:focus,
        .p-password input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .p-password {
          width: 100%;
          display: block;
        }

        .p-password .p-inputtext {
          width: 100%;
        }

        .submit-button {
          width: 100%;
          background: #3b82f6;
          border: none;
          padding: 0.75rem;
          border-radius: 0.5rem;
          color: white;
          font-weight: 500;
        }

        .submit-button:hover:not(:disabled) {
          background: #2563eb;
        }

        .submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .divider-container {
          position: relative;
          margin: 1.5rem 0;
          text-align: center;
        }

        .divider-line {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
        }

        .divider-line-inner {
          width: 100%;
          border-top: 1px solid #d1d5db;
        }

        .divider-text {
          position: relative;
          padding: 0 1rem;
          background: white;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .social-buttons {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.75rem;
        }

        .social-button {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.75rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 0.5rem;
          background: white;
          cursor: pointer;
          transition: background 0.2s;
        }

        .social-button:hover {
          background: #f9fafb;
        }

        .signup-text {
          text-align: center;
          color: #4b5563;
          font-size: 0.875rem;
          margin-top: 1.5rem;
        }

        .signup-link {
          color: #3b82f6;
          font-weight: 500;
          cursor: pointer;
          text-decoration: none;
        }

        .signup-link:hover {
          color: #2563eb;
        }

        .p-message {
          margin-bottom: 1.5rem;
        }

        @media (max-width: 768px) {
          .login-card {
            flex-direction: column;
          }

          .login-left,
          .login-right {
            width: 100%;
          }

          .login-left {
            min-height: 300px;
          }
        }
      `}</style>

      <div className="login-container">
        <div className="login-card">
          {/* Left Panel - Animated Gradient */}
          <div className="login-left animated-gradient">
            <div className="swirl-layer-1"></div>
            <div className="swirl-layer-2"></div>

            <div className="login-left-content">
              <div>
                <svg className="brand-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L12 12M12 12L12 22M12 12L22 12M12 12L2 12" />
                </svg>
              </div>

              <div>
                <p className="tagline">You can easily</p>
                <h1 className="main-heading">
                  Get access your personal hub for clarity and productivity
                </h1>
              </div>
            </div>
          </div>

          {/* Right Panel - Form */}
          <div className="login-right">
            <div>
              <svg className="form-title-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L12 12M12 12L12 22M12 12L22 12M12 12L2 12" />
              </svg>

              <h2 className="form-title">Welcome Back</h2>
              <p className="form-subtitle">
                Sign in to SkoolPillar EPS and keep everything flowing in one place.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <Message severity="error" text={error} style={{ marginBottom: '1.5rem' }} />
            )}

            <div>
              {/* Username Field */}
              <div className="input-wrapper">
                <label className="input-label">Username</label>
                <InputText
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  disabled={loading}
                  required
                />
              </div>

              {/* Password Field */}
              <div className="input-wrapper">
                <label className="input-label">Password</label>
                <Password
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={loading}
                  toggleMask
                  feedback={false}
                  required
                />
              </div>

              {/* Remember Me & Forgot Password */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.25rem',
                fontSize: '0.875rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Checkbox
                    inputId="remember"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.checked || false)}
                    disabled={loading}
                  />
                  <label htmlFor="remember" style={{ color: '#4b5563', cursor: 'pointer' }}>
                    Remember me
                  </label>
                </div>
                <a
                  href="#"
                  style={{
                    color: loading ? '#9ca3af' : '#3b82f6',
                    textDecoration: 'none',
                    fontWeight: '600',
                    pointerEvents: loading ? 'none' : 'auto'
                  }}
                >
                  Forgot Password?
                </a>
              </div>

              {/* Submit Button */}
              <Button
                label={loading ? 'Signing in...' : 'Sign In'}
                icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-sign-in'}
                className="submit-button"
                onClick={handleSubmit}
                disabled={loading}
                loading={loading}
              />

              {/* Sign Up Link */}
              <p className="signup-text">
                Don't have an account?{' '}
                <a href="#" className="signup-link">
                  Contact Administrator
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginScreen;